/**
 * DEMO-ONLY: session overlays for portal devices labels + work-item create.
 * Removable with mock.interceptor.presidio.ts / portal fixtures.
 * Work item # is assigned at submit (stands in for contract integration).
 */
import { CreateWorkItemPayload, DeviceAsset, WorkItem } from '../../presidio-portal/model/presidio-portal.model';

const LABELS_KEY = 'presidio-mock-device-labels';
const CREATED_WI_KEY = 'presidio-mock-created-workitems';
const WI_SEQ_KEY = 'presidio-mock-workitem-seq';

function readJson<T>(key: string, fallback: T): T {
  if (typeof sessionStorage === 'undefined') {
    return fallback;
  }
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(key, JSON.stringify(value));
}

export function readPresidioMockDeviceLabels(): Record<string, string[]> {
  return readJson<Record<string, string[]>>(LABELS_KEY, {});
}

export function writePresidioMockDeviceLabels(deviceId: string, labels: string[]): void {
  const map = readPresidioMockDeviceLabels();
  map[deviceId] = labels;
  writeJson(LABELS_KEY, map);
}

export function applyPresidioMockDeviceLabelOverlay(devices: DeviceAsset[]): DeviceAsset[] {
  const overlay = readPresidioMockDeviceLabels();
  if (!Object.keys(overlay).length) {
    return devices;
  }
  return devices.map(d => (overlay[d.id] ? { ...d, labels: [...overlay[d.id]] } : d));
}

/** @deprecated Delete retired — kept for session cleanup of older demos. */
export function readPresidioMockDeletedWorkItemIds(): string[] {
  return [];
}

/** @deprecated Delete retired (MVP exception to 6.3.11). */
export function markPresidioMockWorkItemDeleted(_id: string): void {
  // no-op
}

export function readPresidioMockCreatedWorkItems(): WorkItem[] {
  return readJson<WorkItem[]>(CREATED_WI_KEY, []);
}

export function persistPresidioMockCreatedWorkItem(item: WorkItem): void {
  const list = readPresidioMockCreatedWorkItems().filter(w => w.id !== item.id);
  writeJson(CREATED_WI_KEY, [item, ...list]);
}

/** DEMO: next display number from contract-integration stand-in. */
function nextPresidioMockWorkItemNumber(): string {
  if (typeof sessionStorage === 'undefined') {
    return `WI-${Date.now()}`;
  }
  const raw = sessionStorage.getItem(WI_SEQ_KEY);
  const next = Math.max(400, Number(raw) || 400) + 1;
  sessionStorage.setItem(WI_SEQ_KEY, String(next));
  return `WI-${next}`;
}

export function buildPresidioMockCreatedWorkItem(payload: CreateWorkItemPayload): WorkItem {
  const now = new Date().toISOString();
  const id = `WI-${Date.now()}`;
  const number = (payload.number || '').trim() || nextPresidioMockWorkItemNumber();
  const attachments = (payload.attachmentNames || [])
    .map(n => n.trim())
    .filter(Boolean)
    .map(fileName => ({ fileName, uploadedAt: now }));
  const {
    type,
    comments,
    attachmentNames: _ignore,
    status: _requestedStatus,
    number: _num,
    resolutionComments,
    ...typed
  } = payload;
  return {
    id,
    number,
    type: type || 'General Inquiry',
    requester: 'Jordan Hale',
    status: 'Pending Presidio',
    submittedDate: now,
    createdDate: now,
    comments: comments || '',
    resolutionComments: resolutionComments || undefined,
    attachments,
    audit: [
      {
        date: now,
        by: 'Jordan Hale',
        type: 'Created',
        description: 'Request submitted — work item number assigned by contract integration',
      },
      {
        date: now,
        by: 'System',
        type: 'Notification',
        description: 'Email sent to Maintenance Contract Specialist',
      },
    ],
    ...typed,
  };
}

export function mergePresidioMockWorkItems(fixtureElements: WorkItem[]): WorkItem[] {
  const overlays = readPresidioMockCreatedWorkItems();
  const overlayById = new Map(overlays.map(w => [w.id, w]));
  const fixtureIds = new Set(fixtureElements.map(w => w.id));
  const newOnly = overlays.filter(w => !fixtureIds.has(w.id));
  const mergedFixture = fixtureElements.map(w => overlayById.get(w.id) || w);
  return [...newOnly, ...mergedFixture];
}

/** Apply a create/update payload onto an existing work item (demo PUT — specialist disposition). */
export function buildPresidioMockUpdatedWorkItem(
  existing: WorkItem,
  payload: CreateWorkItemPayload
): WorkItem {
  const now = new Date().toISOString();
  const attachments = (payload.attachmentNames || [])
    .map(n => n.trim())
    .filter(Boolean)
    .map(fileName => ({ fileName, uploadedAt: now }));
  const {
    type,
    comments,
    attachmentNames: _ignore,
    status: requestedStatus,
    number: requestedNumber,
    resolutionComments,
    ...typed
  } = payload;
  const allowed: WorkItem['status'][] = ['Pending Presidio', 'Completed', 'Rejected'];
  const status: WorkItem['status'] = allowed.includes(requestedStatus as WorkItem['status'])
    ? (requestedStatus as WorkItem['status'])
    : existing.status;
  const statusChanged = status !== existing.status;
  const audit = [...(existing.audit || [])];
  if (statusChanged || !audit.length) {
    audit.push({
      date: now,
      by: 'Maintenance Contract Specialist',
      type: statusChanged ? 'StatusChange' : 'Updated',
      description:
        status === 'Completed'
          ? 'Completed by Maintenance Contract Specialist'
          : status === 'Rejected'
            ? 'Rejected by Maintenance Contract Specialist'
            : `Status set to ${status}`,
    });
  }
  return {
    ...existing,
    ...typed,
    id: existing.id,
    number: requestedNumber || existing.number,
    type: type || existing.type,
    comments: comments ?? existing.comments,
    resolutionComments:
      resolutionComments !== undefined ? resolutionComments : existing.resolutionComments,
    status,
    submittedDate: existing.submittedDate || now,
    createdDate: existing.createdDate || now,
    attachments: attachments.length ? attachments : existing.attachments || [],
    audit,
  };
}
